/**
 * @module core
 *
 * Assembles the `V` object, connects the runtime pieces, and exposes the public API.
 *
 * This file does not initialize anything on its own. The `browser.ts` module
 * calls `start()`, which only runs when the DOM exists. This keeps the package
 * safe to import in Node, Bun, and Deno.
 */

import {
  computed,
  effect,
  EffectScope,
  effectScope,
  flushSync,
  handleError,
  markRaw,
  nextTick,
  reactive,
  ref,
  setErrorHandler,
  shallowRef,
  stop,
  toRaw,
  unref,
  watch,
  watchEffect,
  type Ref,
  type ComputedRef,
} from './reactivity';

import { parse, clearParseCache } from './parser/parser';
import { tokenize, VoodooSyntaxError } from './parser/lexer';
import { evaluate, allowedGlobals, stringify, VoodooRuntimeError } from './parser/interpreter';

import {
  config,
  defineDirective,
  components,
  directives,
  usePlugin,
  PRIORITY,
  type ComponentDefinition,
  type DirectiveHooks,
  type DirectiveBinding,
  type VoodooPlugin,
  type VoodooConfig,
} from './runtime/registry';
import { magic, magics, rootScope, Scope } from './runtime/scope';
import {
  addCleanup,
  destroy,
  evaluateIn,
  findScope,
  getScope,
  markNodeScope,
  parseAttribute,
  refresh,
  setComponentMounter,
  start,
  stopObserving,
  walk,
} from './runtime/walker';
import { defineComponent, instances, mountComponent, setScopeMarker } from './runtime/component';
import { appHost, createApp, setAppHost, setDirectiveRegistrar, type App } from './runtime/app';
import { whenElement, whenReady } from './runtime/boot';
import { installMagics, clipboard, network, screen } from './runtime/magics';
import { installHooks } from './hooks';

import { http, HttpError, request } from './http';
import { createResource } from './http/resource';
import { store, allStores, removeStore, storeNames } from './store';
import { cache, cookie, session, storage, theme, url } from './storage';
import { toast } from './ui/toast';
import { injectStyle, ensureTokens } from './dom/style';
import {
  enter,
  fadeIn,
  fadeOut,
  leave,
  slideDown,
  slideUp,
  viewTransition,
} from './dom/transition';
import * as utils from './utils';

// Side effects: register built-in directives.
import './directives/core';
import './directives/http';

// ---------------------------------------------------------------------------
// Connecting runtime modules
// ---------------------------------------------------------------------------

setComponentMounter(mountComponent);
setScopeMarker(markNodeScope);
setDirectiveRegistrar(directive);
installMagics();
installHooks();

// ---------------------------------------------------------------------------
// Event bus
// ---------------------------------------------------------------------------

type EventHandler = (payload?: any) => void;
const eventBus = new Map<string, Set<EventHandler>>();

/** Subscribes to a global event. Returns a function that cancels the subscription. */
function on(name: string, handler: EventHandler): () => void {
  let set = eventBus.get(name);
  if (!set) eventBus.set(name, (set = new Set()));
  set.add(handler);
  return () => set!.delete(handler);
}

/** Subscribes to a global event for only the next occurrence. */
function onceEvent(name: string, handler: EventHandler): () => void {
  const off = on(name, (payload) => {
    off();
    handler(payload);
  });
  return off;
}

/** Emits a global event. */
function emit(name: string, payload?: unknown): void {
  const set = eventBus.get(name);
  if (!set) return;
  for (const handler of [...set]) {
    try {
      handler(payload);
    } catch (err) {
      handleError(err, `event "${name}"`);
    }
  }
}

function off(name: string, handler?: EventHandler): void {
  if (!handler) {
    eventBus.delete(name);
    return;
  }
  eventBus.get(name)?.delete(handler);
}

// ---------------------------------------------------------------------------
// Custom directives with lifecycle hooks
// ---------------------------------------------------------------------------

/**
 * Registers a custom directive.
 *
 * ```js
 * V.directive('highlight', {
 *   mounted(el, binding) { el.style.background = binding.value },
 *   updated(el, binding) { el.style.background = binding.value }
 * })
 * ```
 *
 * ```html
 * <div v-highlight="'yellow'">Highlight</div>
 * ```
 *
 * Also accepts a short function, called in both `mounted` and `updated`:
 *
 * ```js
 * V.directive('highlight', (el, binding) => { el.style.background = binding.value })
 * ```
 */
function directive<T = any>(
  name: string,
  definition: DirectiveHooks<T> | ((el: HTMLElement, binding: DirectiveBinding<T>) => void)
): void {
  const hooks: DirectiveHooks<T> =
    typeof definition === 'function' ? { mounted: definition, updated: definition } : definition;

  defineDirective(
    name,
    (ctx) => {
      let oldValue: T | undefined;
      let mounted = false;

      const makeBinding = (value: T): DirectiveBinding<T> => ({
        el: ctx.el,
        value,
        oldValue,
        arg: ctx.arg,
        modifiers: ctx.modifiers,
        expression: ctx.expression,
        scope: ctx.scope,
        instance: ctx.scope.owner?.component ?? null,
      });

      const initial = hooks.raw ? (ctx.expression as unknown as T) : ctx.evaluate<T>();
      hooks.created?.(ctx.el, makeBinding(initial));
      hooks.beforeMount?.(ctx.el, makeBinding(initial));

      ctx.effect(() => {
        const value = hooks.raw ? (ctx.expression as unknown as T) : ctx.evaluate<T>();
        if (!mounted) {
          mounted = true;
          oldValue = value;
          hooks.mounted?.(ctx.el, makeBinding(value));
          return;
        }
        if (value === oldValue) return;
        const binding = makeBinding(value);
        hooks.updated?.(ctx.el, binding);
        oldValue = value;
      });

      ctx.cleanup(() => {
        const binding = makeBinding(oldValue as T);
        hooks.beforeUnmount?.(ctx.el, binding);
        hooks.unmounted?.(ctx.el, binding);
      });
    },
    { priority: hooks.priority ?? PRIORITY.DEFAULT, terminal: hooks.terminal ?? false }
  );
}

// ---------------------------------------------------------------------------
// Global state declared by JavaScript
// ---------------------------------------------------------------------------

/**
 * Places values in the root scope, visible to any expression on the page.
 *
 * ```js
 * V.data({ user: null, loading: false })
 * ```
 */
function data<T extends Record<string, unknown>>(values: T): T {
  // Copy by descriptor for the same reason as store: `Object.assign` would call
  // the getter and store the result, turning a derived value into a fixed number.
  // With descriptors, `get total() { ... }` keeps recalculating.
  Object.defineProperties(rootScope.data, Object.getOwnPropertyDescriptors(values));
  return rootScope.data as T;
}

// ---------------------------------------------------------------------------
// V object
// ---------------------------------------------------------------------------

export interface VoodooStatic {
  (selector: unknown, context?: ParentNode): any;
  version: string;
  config: VoodooConfig;
}

/** Published version. */
export const version = '1.1.0';

/**
 * Core of Voodoo. The exported object is also callable: `V('#app')` returns
 * a chainable collection of elements.
 */
export const core = {
  // Utilities first: Voodoo's own names can override.
  ...utils,

  version,
  config,

  // Reactivity
  reactive,
  ref,
  shallowRef,
  computed,
  effect,
  watch,
  watchEffect,
  nextTick,
  toRaw,
  markRaw,
  unref,
  stop,
  effectScope,
  EffectScope,
  flushSync,

  // State
  data,
  store,
  stores: allStores,
  removeStore,
  storeNames,
  scope: rootScope,

  // Components and directives
  component: defineComponent,
  components,
  directive,
  directives,
  magic,
  magics,

  // Application mode
  createApp,

  // DOM lifecycle
  start,
  whenReady,
  whenElement,
  walk,
  refresh,
  destroy,
  stopObserving,
  getScope,
  findScope,
  addCleanup,
  parseAttribute,

  // Expressions
  parse,
  tokenize,
  evaluate,
  evaluateIn,
  stringify,
  clearParseCache,
  globals: allowedGlobals,

  // Services
  http,
  request,
  HttpError,
  /** Reactive resource via JavaScript, equivalent to `v-resource`. */
  resource: createResource,
  toast,
  storage,
  session,
  cookie,
  cache,
  url,
  theme,
  clipboard,
  screen,
  network,

  // Animation
  enter,
  leave,
  fadeIn,
  fadeOut,
  slideUp,
  slideDown,
  viewTransition,

  // Styling
  injectStyle,
  ensureTokens,

  // Global events
  on,
  once: onceEvent,
  off,
  emit,

  // Plugins
  use(plugin: VoodooPlugin | ((V: any) => void), options?: Record<string, unknown>): void {
    // The public `V`, not this object. Handed the core, a plugin found no
    // `V.modal`, `V.router`, `V.i18n` or `V.renderChart` (the entry point adds
    // those on top), and whatever it set, `V.api` say, never reached the global.
    usePlugin(appHost() ?? core, plugin, options);
  },

  /** Defines error handling for the entire application. */
  onError(handler: (err: unknown, context: string) => void): void {
    setErrorHandler(handler);
  },

  /** Mounted component instances for inspection. */
  instances,

  Scope,
  PRIORITY,
  VoodooSyntaxError,
  VoodooRuntimeError,
};

// The application created by `createApp` needs to reach `V` itself to
// install plugins with `app.use(...)`.
setAppHost(core);

export type Core = typeof core;

/** Called by each entry point once its public `V` is complete. */
export { setAppHost as publishV };
export type {
  App,
  ComponentDefinition,
  DirectiveHooks,
  DirectiveBinding,
  VoodooPlugin,
  VoodooConfig,
  Ref,
  ComputedRef,
  Scope as ScopeType,
};
